import React, { memo } from 'react';
import { Box, Table, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useAppSelector } from 'redux/hooks';
import { getClassificationName } from 'utils/common.helper';
import CLSFCN from 'constants/classification';
import NormalButton from 'components/system/atoms/buttons/NormalButton';
import { showAreaDetail } from 'customHooks/useShowAreaConf';

/**
 * AreaConfInfoButton Component
 * エリア別設定画面遷移ボタン
 * @author takuya.tomisato
 *
 * @param props
 * @returns {*}
 * @constructor
 */

// makeStylesでstyleを定義
const useStyles = makeStyles()((theme) => ({
  buttonM: {
    // position: 'relative',
    color: theme.palette.common.black,
    borderRadius: '8px',
    background: '#EBEBEB',
    height: '268px',
    width: '552px',
    fontSize: '27px',
    textAlign: 'center',
    lineHeight: '28px',
    boxShadow: '5px 5px 3px 0px rgba(0, 0, 0, 0.2)',
    '&:hover': {
      background: '#EBEBEB',
      boxShadow: '5px 5px 3px 0px rgba(0, 0, 0, 0.2)',
    },
  },
  frame: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    position: 'relative',
    transform: 'translateY(30%)',
    fontSize: 22,
  },
  child: {
    margin: '0 0px 0px 0',
  },
  detailBlock: {
    borderCollapse: 'collapse',
    fontSize: '19px',
    display: 'block',
  },
  detailBlockBottom: {
    borderCollapse: 'collapse',
    fontSize: '19px',
    display: 'block',
    marginTop: '2px',
  },
  detailInline: {
    borderCollapse: 'collapse',
    fontSize: '19px',
    display: 'inline',
  },
  areaTop: {
    backgroundColor: '#D2F9FF',
    width: '105px',
    height: '32px',
    border: '1px solid #707070',
  },
  areaTopValue: {
    backgroundColor: '#ffffff',
    border: '1px solid #707070',
    width: '48px',
    height: '32px',
  },
  areaBottom: {
    backgroundColor: '#D2F9FF',
    border: '1px solid #707070',
    height: '50px',
    width: '48px',
    lineHeight: '22px',
    verticalAlign: 'middle',
  },
  areaBottomValue: {
    backgroundColor: '#ffffff',
    border: '1px solid #707070',
    width: '48px',
    height: '35px',
    verticalAlign: 'middle',
  },
  bgWhite: {
    background: '#ffffff',
    width: '38px',
    height: '24px',
    border: '1px solid #707070',
    marginLeft: '3px',
  },
  bgGreen: {
    background: '#C9F2E2',
    width: '38px',
    height: '24px',
    border: '1px solid #707070',
    marginLeft: '3px',
  },
  bgPurple: {
    background: '#EBCFEB',
    width: '38px',
    height: '24px',
    border: '1px solid #707070',
    marginLeft: '3px',
  },
  bgYellow: {
    background: '#FFFDB5',
    width: '38px',
    height: '24px',
    border: '1px solid #707070',
    marginLeft: '3px',
  },
  bgRed: {
    background: '#FFC8C8',
    width: '38px',
    height: '24px',
    border: '1px solid #707070',
    marginLeft: '3px',
  },
  bgBlue: {
    background: '#B4DFFF',
    width: '38px',
    height: '24px',
    border: '1px solid #707070',
    marginLeft: '3px',
  },
}));

type ButtonProps = {
  title: string;
  onClick?: () => void;
};

// コンポーネント定義
const AreaConfInfoButton: React.FC<React.PropsWithChildren<ButtonProps>> = (props) => {
  /* ************ state/redux ************ */
  const areaConf = useAppSelector((state) => state.machineConf.machineConf.areaConf);
  const operationConf = useAppSelector((state) => state.machineConf.machineConf.operationConf);
  const machineVersion = useAppSelector((state) => state.machineConf.selectedStation.machineVersion);

  // makeStyleで定義したstyleを取得
  const { classes } = useStyles();

  // 各エリアの表示可否を取得
  if (!areaConf || !operationConf) return null;
  // confのmachineVersionは、保存設定反映・はじめから設定で変更される場合があるため、selectedStationの値を参照する
  const areaDetail = showAreaDetail(areaConf, operationConf, machineVersion);

  const {
    lineFlg1,
    AreaSpecificSettingsLine2,
    AreaSpecificSettingsLine3,
    AreaSpecificSettingsLine4,
    AreaSpecificSettingsLine5,
    AreaSpecificSettingsLine6,
  } = areaDetail.showLine;

  // propsのプロパティ情報を取得
  const { title, onClick } = props;

  return (
    <NormalButton variant="contained" className={classes.buttonM} onClick={onClick}>
      <Box className={classes.frame}>
        <Box className={classes.title}>
          <b>{title}</b>
        </Box>
        <Box className={classes.child}>
          <Box>
            <Table className={classes.detailBlock}>
              <TableRow>
                <td className={classes.areaTop}>初期高さ</td>
                <td className={classes.areaTopValue}>{areaConf?.initialHeight}</td>
              </TableRow>
              <TableRow>
                <td className={classes.areaTop}>アーム開度</td>
                <td className={classes.areaTopValue}>{areaConf?.armOpen}%</td>
              </TableRow>
            </Table>
            <Table className={classes.detailBlockBottom}>
              <Box className={classes.detailInline}>
                <TableRow>
                  <td className={classes.areaBottom}>ｴﾘｱ</td>
                  <td className={classes.areaBottom}>
                    左<br />
                    ｱｰﾑ
                  </td>
                  <td className={classes.areaBottom}>
                    右<br />
                    ｱｰﾑ
                  </td>
                  <td className={classes.areaBottom}>
                    下降
                    <br />
                    限界
                  </td>
                  <td className={classes.areaBottom}>
                    押込
                    <br />力
                  </td>
                </TableRow>
                <TableRow>
                  <td className={classes.areaBottomValue}>
                    <Box className={classes.bgWhite}>&nbsp;</Box>
                  </td>
                  <td className={classes.areaBottomValue}>{lineFlg1 === true ? areaConf?.lPower1 : '－'}</td>
                  <td className={classes.areaBottomValue}>{lineFlg1 === true ? areaConf?.rPower1 : '－'}</td>
                  <td className={classes.areaBottomValue}>{lineFlg1 === true ? areaConf?.descentLimit1 : '－'}</td>
                  <td className={classes.areaBottomValue}>
                    {lineFlg1 === true
                      ? getClassificationName(CLSFCN.AREA_CONFIG.PUSHING_CATEGORY_CD, String(areaConf?.pushing1))
                      : '－'}
                  </td>
                </TableRow>
                <TableRow>
                  <td className={classes.areaBottomValue}>
                    <Box className={classes.bgGreen}>&nbsp;</Box>
                  </td>
                  <td className={classes.areaBottomValue}>{AreaSpecificSettingsLine2 ? areaConf?.lPower2 : '－'}</td>
                  <td className={classes.areaBottomValue}>{AreaSpecificSettingsLine2 ? areaConf?.rPower2 : '－'}</td>
                  <td className={classes.areaBottomValue}>
                    {AreaSpecificSettingsLine2 ? areaConf?.descentLimit2 : '－'}
                  </td>
                  <td className={classes.areaBottomValue}>
                    {AreaSpecificSettingsLine2
                      ? getClassificationName(CLSFCN.AREA_CONFIG.PUSHING_CATEGORY_CD, String(areaConf?.pushing2))
                      : '－'}
                  </td>
                </TableRow>
                <TableRow>
                  <td className={classes.areaBottomValue}>
                    <Box className={classes.bgPurple}>&nbsp;</Box>
                  </td>
                  <td className={classes.areaBottomValue}>{AreaSpecificSettingsLine3 ? areaConf?.lPower3 : '－'}</td>
                  <td className={classes.areaBottomValue}>{AreaSpecificSettingsLine3 ? areaConf?.rPower3 : '－'}</td>
                  <td className={classes.areaBottomValue}>
                    {AreaSpecificSettingsLine3 ? areaConf?.descentLimit3 : '－'}
                  </td>
                  <td className={classes.areaBottomValue}>
                    {AreaSpecificSettingsLine3
                      ? getClassificationName(CLSFCN.AREA_CONFIG.PUSHING_CATEGORY_CD, String(areaConf?.pushing3))
                      : '－'}
                  </td>
                </TableRow>
              </Box>
              &nbsp;&nbsp;&nbsp;
              <Box className={classes.detailInline}>
                <TableRow>
                  <td className={classes.areaBottom}>ｴﾘｱ</td>
                  <td className={classes.areaBottom}>
                    左<br />
                    ｱｰﾑ
                  </td>
                  <td className={classes.areaBottom}>
                    右<br />
                    ｱｰﾑ
                  </td>
                  <td className={classes.areaBottom}>
                    下降
                    <br />
                    限界
                  </td>
                  <td className={classes.areaBottom}>
                    押込
                    <br />力
                  </td>
                </TableRow>
                <TableRow>
                  <td className={classes.areaBottomValue}>
                    <Box className={classes.bgYellow}>&nbsp;</Box>
                  </td>
                  <td className={classes.areaBottomValue}>{AreaSpecificSettingsLine4 ? areaConf?.lPower4 : '－'}</td>
                  <td className={classes.areaBottomValue}>{AreaSpecificSettingsLine4 ? areaConf?.rPower4 : '－'}</td>
                  <td className={classes.areaBottomValue}>
                    {AreaSpecificSettingsLine4 ? areaConf?.descentLimit4 : '－'}
                  </td>
                  <td className={classes.areaBottomValue}>
                    {AreaSpecificSettingsLine4
                      ? getClassificationName(CLSFCN.AREA_CONFIG.PUSHING_CATEGORY_CD, String(areaConf?.pushing4))
                      : '－'}
                  </td>
                </TableRow>
                <TableRow>
                  <td className={classes.areaBottomValue}>
                    <Box className={classes.bgRed}>&nbsp;</Box>
                  </td>
                  <td className={classes.areaBottomValue}>{AreaSpecificSettingsLine5 ? areaConf?.lPower5 : '－'}</td>
                  <td className={classes.areaBottomValue}>{AreaSpecificSettingsLine5 ? areaConf?.rPower5 : '－'}</td>
                  <td className={classes.areaBottomValue}>
                    {AreaSpecificSettingsLine5 ? areaConf?.descentLimit5 : '－'}
                  </td>
                  <td className={classes.areaBottomValue}>
                    {AreaSpecificSettingsLine5
                      ? getClassificationName(CLSFCN.AREA_CONFIG.PUSHING_CATEGORY_CD, String(areaConf?.pushing5))
                      : '－'}
                  </td>
                </TableRow>
                <TableRow>
                  <td className={classes.areaBottomValue}>
                    <Box className={classes.bgBlue}>&nbsp;</Box>
                  </td>
                  <td className={classes.areaBottomValue}>{AreaSpecificSettingsLine6 ? areaConf?.lPower6 : '－'}</td>
                  <td className={classes.areaBottomValue}>{AreaSpecificSettingsLine6 ? areaConf?.rPower6 : '－'}</td>
                  <td className={classes.areaBottomValue}>
                    {AreaSpecificSettingsLine6 ? areaConf?.descentLimit6 : '－'}
                  </td>
                  <td className={classes.areaBottomValue}>
                    {AreaSpecificSettingsLine6
                      ? getClassificationName(CLSFCN.AREA_CONFIG.PUSHING_CATEGORY_CD, String(areaConf?.pushing6))
                      : '－'}
                  </td>
                </TableRow>
              </Box>
            </Table>
          </Box>
        </Box>
      </Box>
    </NormalButton>
  );
};

// レンダーが前回と変わらない場合前回のレンダーを再利用
export default memo(AreaConfInfoButton);
